import { useState, useMemo, useCallback } from 'react';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import pipedriveService, {
  PipedriveConnectionStatus,
  PipedriveDeal,
  PipedriveDealStage,
} from '@/services/pipedrive';
import CEOxPipelineComponent, { DateFilter, DatePreset } from './CEOxPipeline.component';

function getYearRange(year: number): { startDate: string; endDate: string } {
  return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
}

function getMonthRange(offset: number): { startDate: string; endDate: string } {
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const end = new Date(target.getFullYear(), target.getMonth() + 1, 0);
  return { startDate: target.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
}

function getQuarterRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), q * 3, 1);
  const end = new Date(now.getFullYear(), q * 3 + 3, 0);
  return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
}

const CEOxPipelineContainer = function (): JSX.Element {
  const orgId = getActiveOrgId();
  const now = new Date();
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    preset: 'this_year',
    ...getYearRange(now.getFullYear()),
  });

  const handlePresetChange = useCallback((preset: DatePreset) => {
    const year = new Date().getFullYear();
    switch (preset) {
      case 'this_month': setDateFilter({ preset, ...getMonthRange(0) }); break;
      case 'last_month': setDateFilter({ preset, ...getMonthRange(-1) }); break;
      case 'this_quarter': setDateFilter({ preset, ...getQuarterRange() }); break;
      case 'this_year': setDateFilter({ preset, ...getYearRange(year) }); break;
      case 'last_year': setDateFilter({ preset, ...getYearRange(year - 1) }); break;
      default: setDateFilter((prev) => ({ ...prev, preset }));
    }
  }, []);

  const handleCustomDateChange = useCallback((startDate: string, endDate: string) => {
    setDateFilter({ preset: 'custom', startDate, endDate });
  }, []);

  const { data: pipedriveStatus } = useSeoFetch<PipedriveConnectionStatus>(
    ['ceox-pipeline-status', orgId],
    () => pipedriveService.getConnectionStatus(),
    { retry: false }
  );

  const isConnected = pipedriveStatus?.isConnected ?? false;

  const { data: allDeals = [], isLoading } = useSeoFetch<PipedriveDeal[]>(
    ['ceox-pipeline-deals', orgId],
    () => pipedriveService.fetchDeals(),
    { retry: false, enabled: isConnected }
  );

  const { data: stages = [] } = useSeoFetch<PipedriveDealStage[]>(
    ['ceox-pipeline-stages', orgId],
    () => pipedriveService.fetchStages(),
    { retry: false, enabled: isConnected }
  );

  const stageMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of stages) map.set(s.id, s.name);
    return map;
  }, [stages]);

  // Filter deals by date range and enrich with stage names
  const filteredDeals = useMemo(() => {
    const { startDate, endDate } = dateFilter;
    return allDeals
      .map((d) => ({
        ...d,
        stageName: stageMap.get(d.stageId) ?? `Stage ${d.stageId}`,
      }))
      .filter((d) => {
        const dealDate = d.addTime?.split(' ')[0] ?? d.addTime?.split('T')[0] ?? '';
        return dealDate >= startDate && dealDate <= endDate;
      });
  }, [allDeals, stageMap, dateFilter]);

  return (
    <CEOxPipelineComponent
      isPipedriveConnected={isConnected}
      deals={filteredDeals}
      isLoading={isLoading}
      dateFilter={dateFilter}
      onPresetChange={handlePresetChange}
      onCustomDateChange={handleCustomDateChange}
    />
  );
};

export default CEOxPipelineContainer;
